import { useEffect, useRef, useState } from 'react';
import { RichText } from '../i18n/RichText';
import { caughtErrorMessage, serverErrorMessage } from '../i18n/server-errors';
import { t } from '../services/language-manager';
import { freshRowKeys, registerResumeDraft, resumeDraft } from '../services/reauth-resume';
import { PREVIEW_ALLOWED_TYPES, PREVIEW_BILLED_TYPES, previewAction, previewDivisions } from '../dev/preview-sample';
import {
  BUSINESS_CONTEXT_MAX_LENGTH,
  deleteOrgAction,
  getOrgActions,
  isCustomFieldsReasonCode,
  readInfoFields,
  saveOrgAction,
  testActionWebhook,
  WEBHOOK_SECRET_MAX_LENGTH,
  type ActionInstanceSave,
  type CatalogSummary,
  type OrgActionInstance,
  type OrgActionSaveFailure,
  type TestWebhookResult,
  type WebhookTestForm,
} from '../services/wizard-backend';
import {
  toEditableCustomField,
  toWireCustomField,
  validateCustomFields,
  type EditableCustomField,
} from '../services/actions-custom-fields';
import {
  findInfoDivisionConflict,
  infoAttributeKeys,
  infoConflictMessage,
  toEditableInfoField,
  toWireInfoField,
  validateInfoFields,
  type EditableInfoField,
} from '../services/actions-info-fields';
import {
  ALL_ACTION_TYPES_FILTER,
  actionTypeFilterOptions,
  actionTypeLabel,
  appliedActionTypeFilter,
  DEFAULT_ACTION_TYPE,
  isAiReply,
  isConversationInfo,
  isSendFiles,
  offersActionTypeFilter,
  usesCatalog,
} from '../services/action-types';
import {
  ALL_DIVISIONS_FILTER,
  appliedDivisionFilter,
  divisionFilterOptions,
  visibleConfiguredActions,
  type ActionSort,
} from '../services/actions-list-view';
import { DEFAULT_REPLY_LANGUAGE, isReplyLanguage } from '../services/reply-languages';
import { listDivisions, type GenesysDivision } from '../services/genesys-api';
import {
  ActionInstanceCard,
  DIVISION_ID_RE,
  effectiveDivisionIds,
  webhookTestNeed,
  type ActionField,
} from './ActionInstanceCard';
import type { CustomFieldsServerError } from './CustomFieldsEditor';
import type { InfoFieldsServerError } from './InfoFieldsEditor';
import { confirmDialog } from './ConfirmDialog';
import { RecentDeliveriesCard } from './RecentDeliveriesCard';
import { AddActionDialog } from './AddActionDialog';
import { ACTIONS_GUIDE_URL } from '../config/wizard-config';
import { catalogLoaded, enableRequirements } from './ActionReadiness';
import { nextSaveMessage, SAVE_MESSAGE_MS, SAVED_CONFIRMATION_MS, type SaveMessage } from './SaveFeedback';

interface ConversationActionsSectionProps {
  genesysOrgId: string;
  genesysRegion: string;
  /** Insights dashboard URL, passed through to the Recent deliveries card. */
  insightsUrl?: string;
  /** Dev preview: one sample action, no Genesys or API calls. */
  preview?: boolean;
}

/** Local editable state for one instance (secret is write-only, seconds in UI). */
export interface EditableAction {
  key: string;
  actionId?: string;
  /** Chosen on creation (see services/action-types.ts); read-only once saved. */
  type: string;
  label: string;
  enabled: boolean;
  webhookUrl: string;
  authType: 'none' | 'bearer' | 'header';
  secret: string;
  hasSecret: boolean;
  headerName: string;
  timeoutSeconds: number;
  divisionIds: string[];
  /** Manual comma-separated input used when the divisions list is unavailable. */
  divisionsText: string;
  businessContext: string;
  /** Display order = array order; sent as-is. */
  customFields: EditableCustomField[];
  /** AI_REPLY fallback reply language; sent only for that type. */
  replyLanguage: string;
  /** CONVERSATION_INFO fields; display order = array order; sent only for that type. */
  infoFields: EditableInfoField[];
  catalogSummary: CatalogSummary | null;
  createdAt?: string;
  /** Last successful test of the SAVED webhook settings (server); null when none or since changed. */
  webhookTestedAt: string | null;
  /** Server admin-edit counter of the saved action; 0 for a new one. */
  version: number;
  /** Local edit time (ms). Used for Last modified until the server stamps one. */
  touchedAt?: number;
}

let focusNonce = 0;
let nextKey = 0;
function newKey(): string {
  nextKey += 1;
  return `action-${nextKey}`;
}

function toEditable(instance: OrgActionInstance): EditableAction {
  return {
    key: newKey(),
    actionId: instance.actionId,
    type: instance.type || DEFAULT_ACTION_TYPE,
    label: instance.label,
    enabled: instance.enabled,
    webhookUrl: instance.webhookUrl,
    authType: instance.authType,
    secret: '',
    hasSecret: instance.hasSecret,
    headerName: instance.headerName,
    timeoutSeconds: Math.round((instance.timeoutMs || 10000) / 1000),
    divisionIds: instance.divisionIds,
    divisionsText: instance.divisionIds.join(', '),
    businessContext: instance.businessContext,
    customFields: instance.customFields.map(toEditableCustomField),
    replyLanguage: instance.replyLanguage || DEFAULT_REPLY_LANGUAGE,
    infoFields: instance.infoFields.map(toEditableInfoField),
    catalogSummary: instance.catalogSummary,
    createdAt: instance.createdAt,
    webhookTestedAt: instance.webhookTestedAt,
    version: instance.version,
  };
}

/** A new action in the Add action dialog: no type yet, Enabled/Disabled chosen at Create. */
function emptyAction(): EditableAction {
  return {
    key: newKey(),
    type: '',
    label: '',
    enabled: false,
    webhookUrl: '',
    authType: 'none',
    secret: '',
    hasSecret: false,
    headerName: '',
    timeoutSeconds: 10,
    divisionIds: [],
    divisionsText: '',
    businessContext: '',
    customFields: [],
    replyLanguage: DEFAULT_REPLY_LANGUAGE,
    infoFields: [],
    catalogSummary: null,
    createdAt: new Date().toISOString(),
    webhookTestedAt: null,
    version: 0,
    touchedAt: Date.now(),
  };
}

/** Unsaved edits kept across a sign-in. No secret and no webhook test token. */
interface ActionsDraft {
  edited: EditableAction[];
  addDraft: EditableAction | null;
  expandedActionId: string | null;
  /** Saved actions, and the new one, whose typed secret is not kept; the admin is told to type it again. */
  secretTypedActionIds?: string[];
  addDraftSecretTyped?: boolean;
}

function isDraftAction(value: unknown): value is EditableAction {
  if (!value || typeof value !== 'object') return false;
  const v = value as Partial<EditableAction>;
  return typeof v.type === 'string' && typeof v.label === 'string' && typeof v.version === 'number'
    && Array.isArray(v.customFields) && Array.isArray(v.infoFields) && Array.isArray(v.divisionIds);
}

/**
 * Puts kept edits back on the freshly loaded actions. An edit applies only to
 * the same action at the same version, so a change saved meanwhile elsewhere
 * is never overwritten. Secrets are typed again.
 */
function restoreActionsDraft(loaded: EditableAction[], draft: ActionsDraft | null): {
  actions: EditableAction[];
  dirtyKeys: string[];
  expandedKey: string | null;
  addDraft: EditableAction | null;
  secretRetypeKeys: string[];
} {
  const edited = Array.isArray(draft?.edited) ? draft.edited.filter(isDraftAction) : [];
  const secretTyped = new Set(Array.isArray(draft?.secretTypedActionIds) ? draft.secretTypedActionIds : []);
  const dirtyKeys: string[] = [];
  const secretRetypeKeys: string[] = [];
  const actions = loaded.map((action) => {
    const edit = edited.find((e) => e.actionId === action.actionId);
    if (!edit || edit.version !== action.version || edit.type !== action.type) return action;
    dirtyKeys.push(action.key);
    if (action.actionId && secretTyped.has(action.actionId)) secretRetypeKeys.push(action.key);
    return {
      ...action,
      ...freshRowKeys(edit),
      key: action.key,
      actionId: action.actionId,
      secret: '',
      hasSecret: action.hasSecret,
      catalogSummary: action.catalogSummary,
      createdAt: action.createdAt,
      webhookTestedAt: action.webhookTestedAt,
      version: action.version,
    };
  });
  const expandedKey = dirtyKeys[0]
    ?? loaded.find((a) => a.actionId && a.actionId === draft?.expandedActionId)?.key
    ?? null;
  const addDraft = isDraftAction(draft?.addDraft)
    ? { ...emptyAction(), ...freshRowKeys(draft.addDraft), key: newKey(), actionId: undefined, secret: '', hasSecret: false, version: 0 }
    : null;
  if (addDraft && draft?.addDraftSecretTyped === true) secretRetypeKeys.push(addDraft.key);
  return { actions, dirtyKeys, expandedKey, addDraft, secretRetypeKeys };
}

/**
 * What a webhook test covers: URL, auth, header, timeout and secret (the
 * toolbar's fingerprint). Any change means the last test no longer applies.
 */
function webhookSettingsKey(action: EditableAction): string {
  return JSON.stringify([
    action.webhookUrl.trim(),
    action.authType,
    action.authType === 'header' ? action.headerName.trim() : '',
    Math.round(action.timeoutSeconds),
    action.secret,
  ]);
}

/** False while a new action still holds emptyAction()'s values, i.e. the admin has entered nothing. */
function hasDraftInput(action: EditableAction): boolean {
  return Boolean(
    action.type
      || action.label.trim()
      || action.webhookUrl.trim()
      || action.secret
      || action.headerName.trim()
      || action.businessContext.trim()
      || action.divisionsText.trim()
      || action.divisionIds.length > 0
      || action.customFields.length > 0
      || action.infoFields.length > 0
      || action.authType !== 'none'
      || action.timeoutSeconds !== 10
      || action.replyLanguage !== DEFAULT_REPLY_LANGUAGE,
  );
}

/** A card error and the input it belongs to (see `ActionField`), if any. */
interface ActionProblem {
  errorKey: string;
  field?: ActionField;
}

/** Returns the first validation problem, or null when valid. */
function validateAction(action: EditableAction, divisionsAvailable: boolean): ActionProblem | null {
  if (!action.type) return { errorKey: 'settings-actions-error-type', field: 'type' };
  if (!action.label.trim()) return { errorKey: 'settings-actions-error-label', field: 'label' };
  // Last in the form, so checked last; same rule as the wizard backend and the toolbar admin API.
  const divisionsProblem: ActionProblem | null = effectiveDivisionIds(action, divisionsAvailable)
    .every((d) => DIVISION_ID_RE.test(d))
    ? null
    : { errorKey: 'settings-actions-error-divisions', field: 'divisions' };
  if (isAiReply(action.type)) {
    // A saved instance may hold a code from a longer toolbar list; it re-saves as is.
    if (!isReplyLanguage(action.replyLanguage) && !(action.actionId && action.replyLanguage)) {
      return { errorKey: 'settings-actions-error-reply-language', field: 'reply-language' };
    }
    if (action.businessContext.trim().length > BUSINESS_CONTEXT_MAX_LENGTH) {
      return { errorKey: 'settings-actions-error-business-context-reply', field: 'context' };
    }
    return divisionsProblem;
  }
  if (isConversationInfo(action.type)) {
    // A field added but not confirmed with Save field is not part of the action yet.
    if (action.infoFields.some((f) => f.unsaved)) {
      return { errorKey: 'settings-actions-error-info-field-unsaved', field: 'info-fields' };
    }
    // The editor shows the per-input problems; the card-level message points at them.
    if (validateInfoFields(action.infoFields).hasErrors) {
      return { errorKey: 'settings-actions-error-info-fields', field: 'info-fields' };
    }
    if (action.enabled && action.infoFields.length === 0) {
      return { errorKey: 'settings-actions-error-info-fields-required', field: 'info-fields' };
    }
    return divisionsProblem;
  }
  const url = action.webhookUrl.trim();
  if (!url.startsWith('https://')) return { errorKey: 'settings-actions-error-webhook', field: 'webhook' };
  try {
    void new URL(url);
  } catch {
    return { errorKey: 'settings-actions-error-webhook', field: 'webhook' };
  }
  if (!action.actionId && !action.secret) return { errorKey: 'settings-actions-error-secret', field: 'secret' };
  if (action.secret.length > WEBHOOK_SECRET_MAX_LENGTH) {
    return { errorKey: 'settings-actions-error-secret-too-long', field: 'secret' };
  }
  // RFC 7230 token, max 100 chars — same rule as the wizard backend and the
  // toolbar admin API, so client validation never passes a name upstream 400s.
  if (action.authType === 'header' && !/^[!#$%&'*+\-.^_`|~0-9A-Za-z]{1,100}$/.test(action.headerName.trim())) {
    return { errorKey: 'settings-actions-error-header-name', field: 'header' };
  }
  if (!Number.isFinite(action.timeoutSeconds) || action.timeoutSeconds < 3 || action.timeoutSeconds > 20) {
    return { errorKey: 'settings-actions-error-timeout', field: 'timeout' };
  }
  if (!isSendFiles(action.type) && action.businessContext.trim().length > BUSINESS_CONTEXT_MAX_LENGTH) {
    return { errorKey: 'settings-actions-error-business-context', field: 'context' };
  }
  // A field added but not confirmed with Save field is not part of the action yet.
  if (action.customFields.some((f) => f.unsaved)) {
    return { errorKey: 'settings-actions-error-custom-field-unsaved', field: 'custom-fields' };
  }
  // The editor shows the per-input problems; the card-level message points at them.
  if (validateCustomFields(action.customFields).hasErrors) {
    return { errorKey: 'settings-actions-error-custom-fields', field: 'custom-fields' };
  }
  return divisionsProblem;
}

function toSavePayload(action: EditableAction, divisionsAvailable: boolean): ActionInstanceSave {
  if (isAiReply(action.type)) {
    // No webhook, secret or custom fields: the wizard backend and the toolbar drop them.
    const reply: ActionInstanceSave = {
      type: action.type,
      label: action.label.trim(),
      enabled: action.enabled,
      divisionIds: effectiveDivisionIds(action, divisionsAvailable),
      // Always sent: an empty string clears the stored instructions.
      businessContext: action.businessContext.trim(),
      replyLanguage: action.replyLanguage,
    };
    if (action.actionId) reply.actionId = action.actionId;
    return reply;
  }
  if (isConversationInfo(action.type)) {
    // No webhook, secret, custom fields or business context: the wizard backend and the toolbar drop them.
    const info: ActionInstanceSave = {
      type: action.type,
      label: action.label.trim(),
      enabled: action.enabled,
      divisionIds: effectiveDivisionIds(action, divisionsAvailable),
      // Always sent, in display order: an empty array clears the stored list.
      infoFields: action.infoFields.map(toWireInfoField),
    };
    if (action.actionId) info.actionId = action.actionId;
    return info;
  }
  const instance: ActionInstanceSave = {
    type: action.type,
    label: action.label.trim(),
    enabled: action.enabled,
    webhookUrl: action.webhookUrl.trim(),
    authType: action.authType,
    timeoutMs: Math.round(action.timeoutSeconds) * 1000,
    divisionIds: effectiveDivisionIds(action, divisionsAvailable),
    // Always sent: an empty string clears a previously stored value. Send-files
    // runs no analysis and hides the field, so it never keeps one.
    businessContext: isSendFiles(action.type) ? '' : action.businessContext.trim(),
    // Always sent: an empty array clears a previously stored list.
    customFields: action.customFields.map(toWireCustomField),
  };
  if (action.actionId) instance.actionId = action.actionId;
  if (action.authType === 'header') instance.headerName = action.headerName.trim();
  if (action.secret) instance.secret = action.secret;
  return instance;
}

/** INFO_FIELDS_INVALID codes (toolbar and wizard backend) shown with their own sentence; the rest read the generic one. */
const INFO_FIELDS_SERVER_MESSAGE_KEYS: Record<string, string> = {
  too_many_fields: 'settings-actions-info-error-total-cap',
  too_many_important: 'settings-actions-info-error-important-cap',
  fields_required: 'settings-actions-error-info-fields-required',
  matches_required: 'settings-actions-info-error-matches-required',
  too_many_matches: 'settings-actions-info-error-matches-max',
  duplicate_equals: 'settings-actions-info-error-equals-duplicate',
  duplicate_condition: 'settings-actions-info-error-condition-duplicate',
  invalid_number: 'settings-actions-info-error-number',
  link_required: 'settings-actions-info-error-link-url-required',
  link_url_required: 'settings-actions-info-error-link-url-required',
  link_url_too_long: 'settings-actions-info-error-link-url-too-long',
  link_url_invalid: 'settings-actions-info-error-link-url-invalid',
  link_url_scheme: 'settings-actions-info-error-link-url-scheme',
  link_url_host_placeholder: 'settings-actions-info-error-link-url-host',
  link_placeholder_invalid: 'settings-actions-info-error-link-placeholder-invalid',
  link_placeholder_required: 'settings-actions-info-error-link-placeholder-required',
  too_many_placeholders: 'settings-actions-info-error-link-placeholders-max',
  too_many_inputs: 'settings-actions-info-error-inputs-cap',
  input_key_reserved: 'settings-actions-info-error-input-key-reserved',
  input_key_duplicate: 'settings-actions-info-error-input-key-duplicate',
  input_max_length_invalid: 'settings-actions-info-error-input-max-length',
  input_options_count: 'settings-actions-info-error-options-count',
  input_option_invalid: 'settings-actions-info-error-option-invalid',
  input_option_duplicate: 'settings-actions-info-error-options-duplicate',
  input_switch_values: 'settings-actions-info-error-switch-values',
};

/**
 * The form's settings as a webhook test sends them, without the secret. Only
 * valid fields confirmed with Save field go into the sample; the rest of the
 * form does not hold up a test.
 */
function webhookTestFields(action: EditableAction, divisionsAvailable: boolean): Omit<WebhookTestForm, 'secret'> {
  const confirmedFields = action.customFields.filter((f) => !f.unsaved);
  const wire = toSavePayload({
    ...action,
    customFields: validateCustomFields(confirmedFields).hasErrors ? [] : confirmedFields,
  }, divisionsAvailable);
  return {
    label: wire.label,
    webhookUrl: wire.webhookUrl ?? '',
    authType: wire.authType ?? 'none',
    ...(wire.headerName ? { headerName: wire.headerName } : {}),
    timeoutMs: wire.timeoutMs ?? 10000,
    customFields: wire.customFields ?? [],
  };
}

/**
 * Conversation Actions settings section: the org's action instances with
 * add/edit/delete. Each save writes one action (wizard PUT /api/org-actions)
 * with the `version` it last loaded or saved, so a change made to the same
 * action in another window is refused, not overwritten, and other actions are
 * never rewritten. Each card has its own Save. A successful Save collapses
 * that card; a failed save leaves it open. The header Enabled/Disabled switch
 * persists immediately and does not collapse the card. A new action is
 * set up in the Add action dialog and joins the list only once it is created,
 * enabled or disabled as the admin chooses there. Removing a saved action
 * persists immediately.
 * Only one card is expanded at a time.
 * When the org may use two or more action types, the list filters by type.
 * That uses `allowedTypes` (the same list as Add action). A Division filter
 * lists current Genesys divisions that at least one saved action names, and
 * hides when there are none. Choosing one shows actions assigned to it and
 * actions with no divisions (every division, so that one too).
 * The two filters compose, then Sort orders that subset.
 * Division allowlist uses the Genesys divisions list when the signed-in
 * admin can read it, and falls back to a manual comma-separated ID input
 * otherwise.
 */
export function ConversationActionsSection({
  genesysOrgId,
  genesysRegion,
  insightsUrl,
  preview = false,
}: ConversationActionsSectionProps) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [featureDisabled, setFeatureDisabled] = useState(false);
  const [allowedTypes, setAllowedTypes] = useState<string[]>([]);
  const [billedTypes, setBilledTypes] = useState<string[]>([]);
  const [actions, setActions] = useState<EditableAction[]>([]);
  const [lastSaved, setLastSaved] = useState<Record<string, EditableAction>>({});
  const [divisions, setDivisions] = useState<GenesysDivision[] | null>(null);
  const [dirtyKeys, setDirtyKeys] = useState<Record<string, boolean>>({});
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  // The id restarts the card's fade when the same text is shown again.
  const [saveMessages, setSaveMessages] = useState<Record<string, SaveMessage>>({});
  const saveMessageTimer = useRef<number | null>(null);
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});
  const [cardErrors, setCardErrors] = useState<Record<string, string>>({});
  const [customFieldErrors, setCustomFieldErrors] = useState<Record<string, CustomFieldsServerError>>({});
  const [infoFieldErrors, setInfoFieldErrors] = useState<Record<string, InfoFieldsServerError>>({});
  // Field to mark and focus after a failed save; the nonce re-focuses on a repeated failure.
  const [errorFocus, setErrorFocus] = useState<Record<string, { field?: ActionField; nonce: number }>>({});
  // Card whose Save button shows the brief "Saved" confirmation.
  const [justSavedKey, setJustSavedKey] = useState<string | null>(null);
  const justSavedTimer = useRef<number | null>(null);
  const [sortBy, setSortBy] = useState<ActionSort>('newest');
  const [typeFilter, setTypeFilter] = useState(ALL_ACTION_TYPES_FILTER);
  const [divisionFilter, setDivisionFilter] = useState(ALL_DIVISIONS_FILTER);
  const typeOptions = actionTypeFilterOptions(allowedTypes);
  const showTypeFilter = offersActionTypeFilter(allowedTypes);
  const appliedType = appliedActionTypeFilter(allowedTypes, typeFilter);
  // Saved ids, not the open form: unticking a division does not hide the card until Save.
  const divisionOptions = divisions
    ? divisionFilterOptions(Object.values(lastSaved), divisions)
    : [];
  const showDivisionFilter = divisionOptions.length > 0;
  const appliedDivision = appliedDivisionFilter(divisionOptions, divisionFilter);
  const visibleActions = visibleConfiguredActions(actions, sortBy, allowedTypes, typeFilter, {
    selectedId: appliedDivision,
    idsOf: (action) => lastSaved[action.key]?.divisionIds ?? action.divisionIds,
    keepKeys: justSavedKey ? new Set([justSavedKey]) : undefined,
  });
  const typeNarrowed = appliedType !== ALL_ACTION_TYPES_FILTER;
  const divisionNarrowed = appliedDivision !== ALL_DIVISIONS_FILTER;
  const filterEmptyKey = typeNarrowed && divisionNarrowed
    ? 'settings-actions-filter-empty-both'
    : divisionNarrowed
      ? 'settings-actions-filter-empty-division'
      : 'settings-actions-filter-empty';
  // The action being set up in the Add action dialog; it is not in `actions` until created.
  const [addDraft, setAddDraft] = useState<EditableAction | null>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  // Bumped to focus Add action after the render that re-enables (or replaces) it.
  const [addFocusNonce, setAddFocusNonce] = useState(0);
  const [announcement, setAnnouncement] = useState('');
  const kept = useRef({ actions, dirtyKeys, addDraft, expandedKey });
  kept.current = { actions, dirtyKeys, addDraft, expandedKey };

  useEffect(() => registerResumeDraft('conversation-actions', (): ActionsDraft | null => {
    const current = kept.current;
    const edited = current.actions.filter((a) => a.actionId && current.dirtyKeys[a.key]);
    const expandedActionId = current.actions.find((a) => a.key === current.expandedKey)?.actionId ?? null;
    if (edited.length === 0 && !current.addDraft && !expandedActionId) return null;
    return {
      edited,
      addDraft: current.addDraft,
      expandedActionId,
      secretTypedActionIds: edited.filter((a) => a.secret).map((a) => a.actionId!),
      addDraftSecretTyped: Boolean(current.addDraft?.secret),
    };
  }), []);
  // Cards whose secret typed before Sign in again was not kept, until saved or discarded.
  const [secretRetype, setSecretRetype] = useState<Record<string, true>>({});
  useEffect(() => {
    setSecretRetype((current) => {
      const keep = Object.keys(current).filter((key) => dirtyKeys[key] || addDraft?.key === key);
      return keep.length === Object.keys(current).length
        ? current
        : Object.fromEntries(keep.map((key) => [key, true as const]));
    });
  }, [dirtyKeys, addDraft]);
  // A successful test of the settings typed in the Add action dialog: the toolbar's token and
  // the settings it covers. Kept in memory only; it counts while those settings are unchanged.
  const [draftTest, setDraftTest] = useState<{ token: string; settings: string } | null>(null);
  const draftWebhookTested = Boolean(addDraft && draftTest && draftTest.settings === webhookSettingsKey(addDraft));
  // The same for a saved card's unsaved webhook settings, by card key: a Save of those
  // exact settings sends the token, so the action stays tested.
  const [formTests, setFormTests] = useState<Record<string, { token: string; settings: string }>>({});
  const formTestFor = (action: EditableAction) => {
    const test = formTests[action.key];
    return test && test.settings === webhookSettingsKey(action) ? test : null;
  };
  const dropFormTest = (key: string) => {
    setFormTests((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  useEffect(() => {
    if (addFocusNonce) addButtonRef.current?.focus();
  }, [addFocusNonce]);

  useEffect(() => () => {
    if (justSavedTimer.current) window.clearTimeout(justSavedTimer.current);
    if (saveMessageTimer.current) window.clearTimeout(saveMessageTimer.current);
  }, []);

  const clearSaveMessage = (key: string) => {
    setSaveMessages((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const showSaveMessage = (key: string, text: string) => {
    const message = nextSaveMessage(text);
    setSaveMessages({ [key]: message });
    if (saveMessageTimer.current) window.clearTimeout(saveMessageTimer.current);
    saveMessageTimer.current = window.setTimeout(() => {
      saveMessageTimer.current = null;
      setSaveMessages((prev) => {
        if (prev[key]?.id !== message.id) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }, SAVE_MESSAGE_MS);
  };

  const flashSaved = (key: string) => {
    if (justSavedTimer.current) window.clearTimeout(justSavedTimer.current);
    setJustSavedKey(key);
    justSavedTimer.current = window.setTimeout(() => {
      justSavedTimer.current = null;
      setJustSavedKey((current) => (current === key ? null : current));
    }, SAVED_CONFIRMATION_MS);
  };

  const focusErrorField = (key: string, field?: ActionField) => {
    focusNonce += 1;
    setErrorFocus({ [key]: { field, nonce: focusNonce } });
  };

  const divisionName = (id: string) => divisions?.find((d) => d.id === id)?.name ?? null;

  /**
   * Conversation info: the sentence for the clash this action would have if it
   * were enabled with these settings, checked against the other SAVED actions
   * (what the toolbar checks a save against); null when there is none.
   */
  const infoConflictFor = (action: EditableAction): string | null => {
    if (!isConversationInfo(action.type)) return null;
    const divisionIds = effectiveDivisionIds(action, divisions !== null);
    const clash = findInfoDivisionConflict(
      { actionId: action.actionId, type: action.type, label: action.label, enabled: true, divisionIds },
      Object.entries(lastSaved).filter(([key]) => key !== action.key).map(([, saved]) => saved),
    );
    return clash ? infoConflictMessage(clash, divisionName) : null;
  };

  /** Conversation info: the attribute names the other saved info actions use, for the input field note. */
  const otherInfoKeysFor = (key: string): ReadonlySet<string> => new Set(
    Object.entries(lastSaved)
      .filter(([savedKey, saved]) => savedKey !== key && isConversationInfo(saved.type))
      .flatMap(([, saved]) => infoAttributeKeys(saved.infoFields)),
  );

  useEffect(() => {
    if (preview) {
      const sample = previewAction();
      setAllowedTypes(PREVIEW_ALLOWED_TYPES);
      setBilledTypes(PREVIEW_BILLED_TYPES);
      setDivisions(previewDivisions());
      setActions([sample]);
      setLastSaved({ [sample.key]: sample });
      setExpandedKey(sample.key);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      // Divisions load is best effort: without the permission the picker falls
      // back to manual division-ID input, per the design contract.
      try {
        const list = await listDivisions();
        if (!cancelled) setDivisions(list);
      } catch (e) {
        console.warn('Divisions list unavailable, falling back to manual input:', e);
        if (!cancelled) setDivisions(null);
      }
    })();
    (async () => {
      try {
        const config = await getOrgActions(genesysOrgId, genesysRegion);
        if (cancelled) return;
        if (!config.aiActionsEnabled) {
          setFeatureDisabled(true);
        } else {
          const loaded = config.actions.map(toEditable);
          setAllowedTypes(config.allowedTypes);
          setBilledTypes(config.billedTypes);
          setLastSaved(Object.fromEntries(loaded.map((a) => [a.key, a])));
          // Edits kept when the session expired come back unsaved, only onto an unchanged action.
          const restored = restoreActionsDraft(loaded, resumeDraft<ActionsDraft>('conversation-actions'));
          setActions(restored.actions);
          if (restored.dirtyKeys.length > 0) {
            setDirtyKeys(Object.fromEntries(restored.dirtyKeys.map((key) => [key, true])));
          }
          if (restored.expandedKey) setExpandedKey(restored.expandedKey);
          if (restored.addDraft) setAddDraft(restored.addDraft);
          if (restored.secretRetypeKeys.length > 0) {
            setSecretRetype(Object.fromEntries(restored.secretRetypeKeys.map((key) => [key, true as const])));
          }
        }
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setLoadError(caughtErrorMessage(e, 'settings-actions-load-error'));
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [genesysOrgId, genesysRegion, preview]);

  const clearCardStatus = (key: string) => {
    clearSaveMessage(key);
    setSaveErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setCardErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setCustomFieldErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setInfoFieldErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setErrorFocus((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setJustSavedKey((current) => (current === key ? null : current));
  };

  const markDirty = (key: string) => {
    setDirtyKeys((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  };

  const clearDirty = (key: string) => {
    setDirtyKeys((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const dropActionLocally = (key: string) => {
    setActions((prev) => prev.filter((a) => a.key !== key));
    setLastSaved((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    clearDirty(key);
    clearCardStatus(key);
    setExpandedKey((current) => (current === key ? null : current));
  };

  const patchAction = (key: string, patch: Partial<EditableAction>) => {
    setActions((prev) => prev.map((a) => (a.key === key ? { ...a, ...patch, touchedAt: Date.now() } : a)));
    markDirty(key);
    clearCardStatus(key);
  };

  /** Opens a failed save's card, unless it is the Add action dialog's draft (not in the list). */
  const revealCard = (key: string) => {
    if (actions.some((a) => a.key === key)) setExpandedKey(key);
  };

  const openAddDialog = () => {
    setAnnouncement('');
    setDraftTest(null);
    setAddDraft(emptyAction());
  };

  const patchDraft = (patch: Partial<EditableAction>) => {
    if (!addDraft) return;
    setAddDraft((prev) => (prev ? { ...prev, ...patch, touchedAt: Date.now() } : prev));
    clearCardStatus(addDraft.key);
  };

  const closeAddDialog = () => {
    if (addDraft) clearCardStatus(addDraft.key);
    setAddDraft(null);
    setDraftTest(null);
    setAddFocusNonce((n) => n + 1);
  };

  /**
   * Test webhook in the Add action dialog: the typed settings, nothing saved.
   * The button is disabled until `webhookTestNeed` passes, so null here means
   * nothing was sent.
   */
  const testDraft = async (): Promise<TestWebhookResult | null> => {
    if (!addDraft || webhookTestNeed(addDraft)) return null;
    const tested = webhookSettingsKey(addDraft);
    const result = await testActionWebhook(genesysOrgId, genesysRegion, {
      draft: {
        type: addDraft.type,
        ...webhookTestFields(addDraft, divisions !== null),
        secret: addDraft.secret,
      },
    });
    setDraftTest(result.success && result.testToken ? { token: result.testToken, settings: tested } : null);
    return result;
  };

  /**
   * Test webhook on a saved card: the settings on the form, saved or not.
   * Unchanged settings test the saved action, and the toolbar stores a success.
   * Otherwise the form's URL, auth, header and timeout go with the actionId,
   * plus the secret only if one was typed (an empty box uses the stored one).
   * Nothing is saved; a success counts once Save stores those same settings.
   */
  const testSavedAction = async (key: string): Promise<TestWebhookResult | null> => {
    const action = actions.find((a) => a.key === key);
    const snapshot = lastSaved[key];
    if (!action?.actionId || !snapshot || webhookTestNeed(action)) return null;
    const tested = webhookSettingsKey(action);
    if (tested === webhookSettingsKey(snapshot)) {
      const result = await testActionWebhook(genesysOrgId, genesysRegion, { actionId: action.actionId });
      // A failure leaves the stored success as is.
      if (result.success) {
        const testedAt = new Date().toISOString();
        setLastSaved((prev) => (prev[key] ? { ...prev, [key]: { ...prev[key], webhookTestedAt: testedAt } } : prev));
        setActions((prev) => prev.map((a) => (a.key === key ? { ...a, webhookTestedAt: testedAt } : a)));
      }
      return result;
    }
    const result = await testActionWebhook(genesysOrgId, genesysRegion, {
      actionId: action.actionId,
      draft: {
        ...webhookTestFields(action, divisions !== null),
        ...(action.secret ? { secret: action.secret } : {}),
      },
    });
    const token = result.success ? result.testToken : null;
    setFormTests((prev) => {
      const next = { ...prev };
      if (token) next[key] = { token, settings: tested };
      else delete next[key];
      return next;
    });
    return result;
  };

  /** Cancel / Escape / backdrop: nothing was created, so the draft is dropped, asking first if the admin entered anything. */
  const cancelAddDialog = async () => {
    if (!addDraft || savingKey) return;
    if (hasDraftInput(addDraft)) {
      const discard = await confirmDialog({
        title: t('settings-actions-discard-confirm-title'),
        subject: addDraft.label.trim() || undefined,
        message: t('settings-actions-delete-confirm-unsaved'),
        confirmLabel: t('settings-actions-discard'),
        cancelLabel: t('settings-actions-discard-keep'),
        danger: true,
      });
      if (!discard) return;
    }
    closeAddDialog();
  };

  /** Shows a failed save on its card (every save is one action). */
  const showSaveFailure = (key: string, result: OrgActionSaveFailure) => {
    if (result.code === 'CUSTOM_FIELDS_INVALID') {
      const detail = result.detail ?? {};
      // A toolbar 400 carries a machine code as `reason`; its `message` is the readable one.
      const reason = detail.reason && !isCustomFieldsReasonCode(detail.reason) ? detail.reason : '';
      setCustomFieldErrors({
        [key]: {
          ...(detail.index !== undefined ? { index: detail.index } : {}),
          ...(detail.field ? { field: detail.field } : {}),
          message: reason
            ? serverErrorMessage({ cause: reason, knownKey: 'settings-actions-fields-error-server', keepDetail: true })
            : t('settings-actions-fields-error-server'),
        },
      });
      setSaveErrors({ [key]: t('settings-actions-save-invalid') });
      revealCard(key);
      focusErrorField(key, 'custom-fields');
      return;
    }
    if (result.code === 'ACTION_TYPE_NOT_ALLOWED') {
      // The card's inline error names the problem, the save line stays the generic "not saved".
      setCardErrors({ [key]: t('settings-actions-error-type-not-allowed') });
      setSaveErrors({ [key]: t('settings-actions-save-invalid') });
      revealCard(key);
      focusErrorField(key, 'type');
      return;
    }
    if (result.code === 'ACTIONS_CONFLICT' && (result.detail?.reason === 'version' || result.detail?.reason === 'deleted')) {
      setSaveErrors({ [key]: t(result.detail.reason === 'deleted' ? 'settings-actions-save-deleted' : 'settings-actions-save-conflict') });
      revealCard(key);
      return;
    }
    if (result.code === 'DIVISION_IDS_INVALID') {
      setCardErrors({ [key]: t('settings-actions-error-divisions') });
      setSaveErrors({ [key]: t('settings-actions-save-invalid') });
      revealCard(key);
      focusErrorField(key, 'divisions');
      return;
    }
    const savingAction = addDraft?.key === key ? addDraft : actions.find((a) => a.key === key);
    if (result.code === 'INFO_FIELDS_INVALID') {
      const detail = result.detail ?? {};
      setInfoFieldErrors({
        [key]: {
          ...(detail.fieldIndex !== undefined ? { fieldIndex: detail.fieldIndex } : {}),
          message: t(INFO_FIELDS_SERVER_MESSAGE_KEYS[detail.code ?? ''] ?? 'settings-actions-info-error-server'),
        },
      });
      setSaveErrors({ [key]: t('settings-actions-save-invalid') });
      revealCard(key);
      focusErrorField(key, 'info-fields');
      return;
    }
    if (result.code === 'INFO_DIVISION_IN_USE') {
      // Another window enabled a clashing action after this page loaded.
      const detail = result.detail ?? {};
      const divisionIds = savingAction ? effectiveDivisionIds(savingAction, divisions !== null) : [];
      const divisionId = detail.divisionId ?? null;
      setCardErrors({
        [key]: infoConflictMessage(
          {
            conflictLabel: detail.conflictLabel ?? '',
            divisionId,
            via: divisionId ? 'shared' : divisionIds.length === 0 ? 'candidate_all' : 'other_all',
          },
          divisionName,
        ),
      });
      setSaveErrors({ [key]: t('settings-actions-save-invalid') });
      revealCard(key);
      focusErrorField(key, 'divisions');
      return;
    }
    if (result.code === 'SECRET_TOO_LONG') {
      setCardErrors({ [key]: t('settings-actions-error-secret-too-long') });
      setSaveErrors({ [key]: t('settings-actions-save-invalid') });
      revealCard(key);
      focusErrorField(key, 'secret');
      return;
    }
    if (result.code === 'CONFIG_TOO_LARGE') {
      setSaveErrors({ [key]: t('settings-actions-error-config-too-large') });
      revealCard(key);
      return;
    }
    if (result.code === 'WIZARD_OUTDATED') {
      setSaveErrors({ [key]: serverErrorMessage({ code: 'WIZARD_OUTDATED' }) });
      revealCard(key);
      return;
    }
    const savingType = savingAction?.type || '';
    const contextTooLong = result.code === 'BUSINESS_CONTEXT_TOO_LONG';
    setSaveErrors({
      [key]: contextTooLong
        ? t(isAiReply(savingType) ? 'settings-actions-error-business-context-reply' : 'settings-actions-error-business-context')
        : serverErrorMessage({
          code: result.code,
          cause: result.cause,
          knownKey: 'settings-actions-save-error',
          keepDetail: result.code !== 'BUSINESS_CONTEXT_TOO_LONG',
        }),
    });
    revealCard(key);
    if (contextTooLong) focusErrorField(key, 'context');
  };

  /**
   * Creates (no actionId) or updates one action, checked against the version it
   * was last loaded or saved at. Returns the stored instance, or null with the
   * reason on the card.
   */
  const persistAction = async (key: string, instance: ActionInstanceSave): Promise<OrgActionInstance | null> => {
    // Dev preview has no org. A Conversation info save still round-trips through the same
    // reader the page uses after a real save, so the stored field type is what comes back.
    if (preview && isConversationInfo(instance.type)) {
      return {
        actionId: instance.actionId || `preview-${key}`,
        type: instance.type,
        label: instance.label,
        enabled: instance.enabled,
        webhookUrl: '',
        authType: 'none',
        hasSecret: false,
        headerName: '',
        timeoutMs: 10000,
        divisionIds: instance.divisionIds,
        businessContext: '',
        customFields: [],
        replyLanguage: '',
        infoFields: readInfoFields(instance.infoFields),
        catalogSummary: null,
        webhookTestedAt: null,
        version: (lastSaved[key]?.version ?? 0) + 1,
      };
    }
    const expectedVersion = instance.actionId ? lastSaved[key]?.version ?? 0 : undefined;
    const result = await saveOrgAction(genesysOrgId, genesysRegion, instance, expectedVersion);
    if (!result.status) {
      showSaveFailure(key, result);
      return null;
    }
    return result.action;
  };

  const deleteAction = async (key: string) => {
    if (savingKey) return;
    const target = actions.find((a) => a.key === key);
    if (!target?.actionId) return;
    const confirmed = await confirmDialog({
      title: t('settings-actions-delete-confirm-title'),
      subject: target.label.trim() || undefined,
      message: t(isAiReply(target.type)
        ? 'settings-actions-delete-confirm-reply'
        : isConversationInfo(target.type) ? 'settings-actions-delete-confirm-info' : 'settings-actions-delete-confirm'),
      confirmLabel: t('settings-actions-delete'),
      cancelLabel: t('confirm-dialog-cancel'),
      danger: true,
    });
    if (!confirmed) return;
    setSavingKey(key);
    clearCardStatus(key);
    try {
      const result = await deleteOrgAction(genesysOrgId, genesysRegion, target.actionId, lastSaved[key]?.version ?? 0);
      if (!result.status) {
        showSaveFailure(key, result);
        return;
      }
      dropActionLocally(key);
    } catch (e) {
      setSaveErrors({ [key]: caughtErrorMessage(e, 'settings-actions-delete-error') });
      setExpandedKey(key);
    } finally {
      setSavingKey(null);
    }
  };

  /**
   * Cancel on a saved card: back to the last-saved state, then collapse. Asks
   * first only when there are unsaved edits, like Cancel on a new card. The
   * catalog summary is kept: an import persists at once and is not an edit.
   */
  const cancelEdits = async (key: string) => {
    if (savingKey) return;
    const snapshot = lastSaved[key];
    if (!snapshot) return;
    if (dirtyKeys[key]) {
      const discard = await confirmDialog({
        title: t('settings-actions-cancel-edits-confirm-title'),
        subject: snapshot.label.trim() || undefined,
        message: t('settings-actions-cancel-edits-confirm'),
        confirmLabel: t('settings-actions-discard'),
        cancelLabel: t('settings-actions-discard-keep'),
        danger: true,
      });
      if (!discard) return;
      setActions((prev) => prev.map((a) => (
        a.key === key ? { ...snapshot, catalogSummary: a.catalogSummary } : a
      )));
      clearDirty(key);
      dropFormTest(key);
    }
    clearCardStatus(key);
    setExpandedKey((current) => (current === key ? null : current));
  };

  /**
   * Header Enabled/Disabled switch. A saved action is persisted at once with
   * only its last-saved `enabled` flag flipped, so the card's other unsaved
   * edits stay local. A new action has no card until it is created (the Add
   * action dialog's Create step chooses); an unsaved one would just take the
   * value for its Save. It does not collapse the card.
   */
  const toggleEnabled = async (key: string, enabled: boolean) => {
    if (savingKey) return;
    const snapshot = lastSaved[key];
    if (!snapshot?.actionId) {
      patchAction(key, { enabled });
      return;
    }
    // Mirrors the card's blocked switch: a webhook action goes on only once tested (and, with a catalog, loaded),
    // a Conversation info action only with a saved field and no other enabled one on its divisions.
    const current = actions.find((a) => a.key === key);
    if (enabled && (enableRequirements(snapshot.type, {
      webhookTested: Boolean(snapshot.webhookTestedAt),
      catalogLoaded: catalogLoaded(current?.catalogSummary ?? null),
      hasInfoFields: snapshot.infoFields.length > 0,
    }).some((r) => !r.done) || infoConflictFor(snapshot))) return;
    setSavingKey(key);
    clearCardStatus(key);
    try {
      const payload = toSavePayload({ ...snapshot, enabled }, divisions !== null);
      const saved = await persistAction(key, payload);
      if (!saved) return;
      const nextSnapshot = { ...toEditable(saved), key };
      setLastSaved((prev) => ({ ...prev, [key]: nextSnapshot }));
      setActions((prev) => prev.map((a) => (a.key === key ? { ...a, enabled: nextSnapshot.enabled } : a)));
      showSaveMessage(key, t(enabled ? 'settings-actions-toggle-on-saved' : 'settings-actions-toggle-off-saved'));
    } catch (e) {
      setSaveErrors({ [key]: caughtErrorMessage(e, 'settings-actions-save-error') });
      setExpandedKey(key);
    } finally {
      setSavingKey(null);
    }
  };

  /**
   * Save one card. An existing action keeps its `enabled`, which only the
   * header switch changes; new actions are created by createDraft. Success
   * collapses the card. The Saved button state and the short alert stay.
   * Failure leaves the card open.
   */
  const handleSave = async (key: string) => {
    if (savingKey) return;
    const action = actions.find((a) => a.key === key);
    if (!action) return;

    const divisionsAvailable = divisions !== null;
    const problem = validateAction(action, divisionsAvailable);
    clearCardStatus(key);
    if (problem) {
      setCardErrors({ [key]: t(problem.errorKey) });
      setSaveErrors({ [key]: t('settings-actions-save-invalid') });
      setExpandedKey(key);
      focusErrorField(key, problem.field);
      return;
    }
    // Save is disabled while it shows; kept for a save started some other way.
    const conflict = action.enabled ? infoConflictFor(action) : null;
    if (conflict) {
      setCardErrors({ [key]: conflict });
      setSaveErrors({ [key]: t('settings-actions-save-invalid') });
      setExpandedKey(key);
      focusErrorField(key, 'divisions');
      return;
    }

    // A successful test of exactly these unsaved settings: the toolbar keeps it if they still match.
    const formTest = formTestFor(action);
    const instance = toSavePayload(action, divisionsAvailable);
    if (formTest) instance.webhookTestToken = formTest.token;

    setSavingKey(key);
    try {
      const saved = await persistAction(key, instance);
      if (!saved) return;
      const nextAction = { ...toEditable(saved), key };
      setActions((prev) => prev.map((a) => (a.key === key ? nextAction : a)));
      setLastSaved((prev) => ({ ...prev, [key]: nextAction }));
      clearDirty(key);
      dropFormTest(key);
      showSaveMessage(key, t('settings-actions-save-success'));
      flashSaved(key);
      // Close only after a successful Save. The Saved button and the short
      // alert stay on the collapsed card. The Enabled switch does not close it.
      setExpandedKey((current) => (current === key ? null : current));
    } catch (e) {
      setSaveErrors({ [key]: caughtErrorMessage(e, 'settings-actions-save-error') });
      setExpandedKey(key);
    } finally {
      setSavingKey(null);
    }
  };

  /**
   * Create step of the Add action dialog: saves the draft with the chosen
   * state and, only then, adds it to the list (collapsed; a webhook action
   * opens on what it still needs before it can be switched on).
   * Resolves false when it was not created, with the reason on the dialog.
   */
  const createDraft = async (requestedEnabled: boolean): Promise<boolean> => {
    if (!addDraft || savingKey) return false;
    const key = addDraft.key;
    // Created switched off while something required is outstanding: the webhook test, and for
    // orders and returns the catalog, which needs the saved action; for Conversation info a field
    // and divisions no other enabled info action uses. Its card tracks them.
    const blocked = enableRequirements(addDraft.type, {
      webhookTested: draftWebhookTested,
      catalogLoaded: false,
      hasInfoFields: addDraft.infoFields.length > 0,
    }).some((r) => !r.done) || Boolean(infoConflictFor(addDraft));
    const action = { ...addDraft, enabled: requestedEnabled && !blocked };
    const divisionsAvailable = divisions !== null;
    const problem = validateAction(action, divisionsAvailable);
    clearCardStatus(key);
    if (problem) {
      setCardErrors({ [key]: t(problem.errorKey) });
      focusErrorField(key, problem.field);
      return false;
    }

    // The toolbar keeps the dialog's test only if these exact settings were tested.
    const draftInstance = toSavePayload(action, divisionsAvailable);
    if (draftWebhookTested && draftTest) draftInstance.webhookTestToken = draftTest.token;

    setSavingKey(key);
    try {
      const saved = await persistAction(key, draftInstance);
      if (!saved) return false;
      const nextAction = { ...toEditable(saved), key };
      setActions((prev) => [nextAction, ...prev]);
      setLastSaved((prev) => ({ ...prev, [key]: nextAction }));
      clearCardStatus(key);
      setAddDraft(null);
      setDraftTest(null);
      flashSaved(key);
      if (blocked) {
        // Open it on what it still needs: the instruction, the webhook test and the catalog upload.
        if (expandedKey) clearSaveMessage(expandedKey);
        setExpandedKey(key);
      }
      setAnnouncement(t(blocked
        ? isConversationInfo(nextAction.type)
          ? 'settings-actions-create-success-blocked-info'
          : usesCatalog(nextAction.type) ? 'settings-actions-create-success-blocked-catalog' : 'settings-actions-create-success-blocked'
        : nextAction.enabled ? 'settings-actions-create-success-enabled' : 'settings-actions-create-success-disabled'));
      setAddFocusNonce((n) => n + 1);
      return true;
    } catch (e) {
      setSaveErrors({ [key]: caughtErrorMessage(e, 'settings-actions-save-error') });
      return false;
    } finally {
      setSavingKey(null);
    }
  };

  const addButton = (
    <button
      type="button"
      ref={addButtonRef}
      className="btn btn--primary actions-add-btn"
      onClick={openAddDialog}
      aria-haspopup="dialog"
      disabled={Boolean(savingKey) || allowedTypes.length === 0}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
      {t('settings-actions-add')}
    </button>
  );

  return (
    <div className="settings-section settings-section--first">
      {loading && <p className="settings-page__status">{t('settings-actions-loading')}</p>}

      {!loading && loadError && (
        <p className="settings-page__error" role="alert">
          {loadError}
        </p>
      )}

      {!loading && !loadError && featureDisabled && (
        <p className="settings-page__notice" role="status">
          <RichText
            k="settings-actions-disabled"
            values={{
              email: (
                <a href={`mailto:${t('settings-actions-disabled-email')}`} dir="ltr">
                  {t('settings-actions-disabled-email')}
                </a>
              ),
            }}
          />
        </p>
      )}

      {!loading && !loadError && !featureDisabled && (
        <>
          {actions.length === 0 ? (
            <div className="actions-empty">
              <p className="actions-empty__text">{t('settings-actions-empty')}</p>
              {addButton}
            </div>
          ) : (
            // Above the list, because a new draft always opens at the top of it.
            <>
              <div className="actions-toolbar">
                {showTypeFilter && (
                  <div className="actions-sort">
                    <label className="actions-sort__label" htmlFor="actions-type-filter">
                      {t('settings-actions-filter-label')}
                    </label>
                    <select
                      id="actions-type-filter"
                      className="form-select actions-sort__select"
                      value={appliedType}
                      onChange={(e) => setTypeFilter(e.currentTarget.value)}
                    >
                      <option value={ALL_ACTION_TYPES_FILTER}>{t('settings-actions-filter-all')}</option>
                      {typeOptions.map((type) => (
                        <option key={type} value={type}>{actionTypeLabel(type)}</option>
                      ))}
                    </select>
                  </div>
                )}
                {showDivisionFilter && (
                  <div className="actions-sort">
                    <label className="actions-sort__label" htmlFor="actions-division-filter">
                      {t('settings-actions-division-filter-label')}
                    </label>
                    <select
                      id="actions-division-filter"
                      className="form-select actions-sort__select"
                      value={appliedDivision}
                      onChange={(e) => setDivisionFilter(e.currentTarget.value)}
                    >
                      <option value={ALL_DIVISIONS_FILTER}>{t('settings-actions-division-filter-all')}</option>
                      {divisionOptions.map((division) => (
                        <option key={division.id} value={division.id} dir="auto">{division.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                {actions.length > 1 && (
                  <div className="actions-sort">
                    <label className="actions-sort__label" htmlFor="actions-sort">
                      {t('settings-actions-sort-label')}
                    </label>
                    <select
                      id="actions-sort"
                      className="form-select actions-sort__select"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.currentTarget.value as ActionSort)}
                    >
                      <option value="newest">{t('settings-actions-sort-newest')}</option>
                      <option value="oldest">{t('settings-actions-sort-oldest')}</option>
                      <option value="name">{t('settings-actions-sort-name')}</option>
                      <option value="type">{t('settings-actions-sort-type')}</option>
                      <option value="modified">{t('settings-actions-sort-modified')}</option>
                    </select>
                  </div>
                )}
                {addButton}
              </div>
              {visibleActions.length === 0 && (
                <div className="actions-empty" role="status">
                  <p className="actions-empty__text">{t(filterEmptyKey)}</p>
                </div>
              )}
            </>
          )}

          {visibleActions.map((action) => (
            <ActionInstanceCard
              key={action.key}
              value={action}
              allowedTypes={allowedTypes}
              billedTypes={billedTypes}
              divisions={divisions}
              genesysOrgId={genesysOrgId}
              genesysRegion={genesysRegion}
              expanded={expandedKey === action.key}
              dirty={Boolean(dirtyKeys[action.key])}
              saving={savingKey === action.key}
              justSaved={justSavedKey === action.key}
              locked={Boolean(savingKey) && savingKey !== action.key}
              error={cardErrors[action.key] || null}
              errorFocus={errorFocus[action.key] || null}
              saveMessage={saveMessages[action.key] || null}
              saveError={saveErrors[action.key] || null}
              customFieldsError={customFieldErrors[action.key] || null}
              infoFieldsError={infoFieldErrors[action.key] || null}
              infoConflict={infoConflictFor(action)}
              enableConflict={lastSaved[action.key] ? infoConflictFor(lastSaved[action.key]) : null}
              infoFieldsSaved={(lastSaved[action.key]?.infoFields.length ?? 0) > 0}
              otherInfoKeys={isConversationInfo(action.type) ? otherInfoKeysFor(action.key) : undefined}
              secretNotRestored={Boolean(secretRetype[action.key])}
              onToggleExpand={() => {
                // Collapsing, reopening, or opening another card (which collapses the current one) drops the success text.
                clearSaveMessage(action.key);
                if (expandedKey) clearSaveMessage(expandedKey);
                setExpandedKey((current) => (current === action.key ? null : action.key));
              }}
              onChange={(patch) => patchAction(action.key, patch)}
              onSave={() => void handleSave(action.key)}
              onDelete={() => void deleteAction(action.key)}
              onCancelEdits={() => void cancelEdits(action.key)}
              onToggleEnabled={(enabled) => void toggleEnabled(action.key, enabled)}
              webhookTested={Boolean(lastSaved[action.key]?.webhookTestedAt)}
              unsavedWebhookTested={Boolean(formTestFor(action))}
              onTest={() => testSavedAction(action.key)}
              onCatalogImported={(summary) => {
                // Catalog import persists upstream immediately; reflect it
                // locally without marking the section dirty.
                setActions((prev) => prev.map((a) => (
                  a.key === action.key ? { ...a, catalogSummary: summary, touchedAt: Date.now() } : a
                )));
              }}
            />
          ))}

          <p className="actions-help">
            <RichText
              k="settings-actions-guide"
              values={{
                guide: (
                  <a
                    href={ACTIONS_GUIDE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('settings-actions-guide-link-label')}
                  >
                    {t('settings-actions-guide-link')}
                  </a>
                ),
              }}
            />
          </p>

          <RecentDeliveriesCard
            genesysOrgId={genesysOrgId}
            genesysRegion={genesysRegion}
            insightsUrl={insightsUrl}
            preview={preview}
            actionLabels={Object.fromEntries(
              actions
                .filter((a): a is EditableAction & { actionId: string } => !!a.actionId)
                .map((a) => [a.actionId, a.label.trim()]),
            )}
            actionTypes={Object.fromEntries(
              actions
                .filter((a): a is EditableAction & { actionId: string } => !!a.actionId)
                .map((a) => [a.actionId, a.type]),
            )}
          />

          <p className="sr-only" role="status">{announcement}</p>

          {addDraft && (() => {
            const draftKey = addDraft.key;
            const problem = addDraft.type ? validateAction(addDraft, divisions !== null) : null;
            const draftError = cardErrors[draftKey] || saveErrors[draftKey] || null;
            const draftConflict = infoConflictFor(addDraft);
            return (
              <AddActionDialog
                allowedTypes={allowedTypes}
                billedTypes={billedTypes}
                typeInputId={`action-type-${draftKey}`}
                type={addDraft.type}
                onTypeChange={(type) => patchDraft({ type })}
                label={addDraft.label}
                invalidType={errorFocus[draftKey]?.field === 'type'}
                blocker={problem ? t(problem.errorKey) : null}
                error={draftError}
                feedbackId={`action-save-feedback-${draftKey}`}
                saving={savingKey === draftKey}
                webhookTested={draftWebhookTested}
                hasInfoFields={addDraft.infoFields.length > 0}
                enableConflict={draftConflict}
                onCancel={() => void cancelAddDialog()}
                onCreate={createDraft}
              >
                <ActionInstanceCard
                  variant="dialog"
                  value={addDraft}
                  allowedTypes={allowedTypes}
                  billedTypes={billedTypes}
                  divisions={divisions}
                  genesysOrgId={genesysOrgId}
                  genesysRegion={genesysRegion}
                  expanded
                  dirty
                  saving={savingKey === draftKey}
                  justSaved={false}
                  locked={false}
                  error={draftError}
                  errorFocus={errorFocus[draftKey] || null}
                  blockedField={problem?.field ?? null}
                  saveMessage={null}
                  saveError={null}
                  customFieldsError={customFieldErrors[draftKey] || null}
                  infoFieldsError={infoFieldErrors[draftKey] || null}
                  infoConflict={draftConflict}
                  enableConflict={null}
                  infoFieldsSaved={false}
                  otherInfoKeys={isConversationInfo(addDraft.type) ? otherInfoKeysFor(draftKey) : undefined}
                  secretNotRestored={Boolean(secretRetype[draftKey])}
                  onToggleExpand={() => {}}
                  onChange={patchDraft}
                  onSave={() => {}}
                  onDelete={() => {}}
                  onCancelEdits={() => {}}
                  onToggleEnabled={() => {}}
                  onCatalogImported={() => {}}
                  webhookTested={draftWebhookTested}
                  onTest={testDraft}
                />
              </AddActionDialog>
            );
          })()}
        </>
      )}
    </div>
  );
}
